/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useState, useEffect, lazy, useMemo, useCallback } from "react"
import { datadogRum } from "@datadog/browser-rum"
import { Switch, Redirect, useHistory, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { isEmpty, isEqual, isUndefined, keyBy } from "lodash"
/**
 * since we're not importing the whole troy-common module (i.e. troy-common/index.js) to save space,
 * we import troy.less here to ensure that troy.less is compiled first before
 * other troy less files, otherwise the ordering goes out-of-whack in the compiled CSS file
 * */
import "troy-common/assets/styles/troy.less"
import { TroySidebarMenu } from "troy-common/sidebar"
import createMenus from "troy-common/sidebar/sidebar-v2/SidebarMenuV2Data"
import {
  INVENTORY_KEY,
  MENU_KEY,
  TROY_CHANGE_LANGUAGE,
  ORDER_KEY,
  FEEDBACK_KEY,
  ECOMMERCE_PRODUCT_KEY,
  STOREFRONT_COLLECTION_KEY,
  LOCATIONS_KEY,
} from "troy-common/constants"
import {
  useLocaleContext,
  useTranslation,
  useTranslationsMap,
} from "common/utils/i18n"
import { sendEvent, EventLabel, TroyWebTracker } from "utils/tracking"
import {
  MENUS,
  CATALOGUES,
  INVENTORY,
  ORDER_MANAGER_URL_PREFIX,
  CREATE_MENU,
  CREATE_CATALOGUE,
  FEEDBACK,
  ECOMMERCE_PRODUCTS,
  ECOMMERCE_PRODUCTS_LIST,
  ECOMMERCE_PRODUCTS_ADD,
  ECOMMERCE_PRODUCTS_BULK_ADD,
  ECOMMERCE_PRODUCTS_EDIT,
  ECOMMERCE_PRODUCTS_DETAIL,
  ECOMMERCE_URL_PREFIX,
  STOREFRONT_COLLECTION_URL_PREFIX,
  STOREFRONT_COLLECTION_LIST,
  STOREFRONT_COLLECTIONS_ADD,
  STOREFRONT_COLLECTIONS_EDIT,
  LOCATIONS,
} from "constants/routes"
import { EventAction, EventCategory } from "constants/tracking"
import {
  EventAction as OrderManagerEventAction,
  EventCategory as OrderManagerEventCategory,
} from "order-manager/constants/tracking"
import {
  EventAction as EcommerceEventAction,
  EventCategory as EcommerceEventCategory,
} from "ecommerce/constants/tracking"
import { sessionInfo, langCode2LangName } from "utils/session"
import { getUCMConfig } from "utils/config"
import { OrderNotificationComponent } from "order-manager/components/OrderNotificationComponent"

import { MenuStoreListWithHeader, MenuEditorWithHeader } from "pages/menu"
import {
  CreateMenuGroupWithHeader,
  MenuGroupListWithHeader,
  MenuGroupEditorWithHeader,
} from "pages/menu-groups"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Messages } from "store/duck/global.type"
import { InventoryListWithHeader } from "pages/inventory"
import { InventoryStoreDetailWithHeader } from "pages/inventory/InventoryDetail"
import FlaggedRoute, { NotFoundRoute } from "components/common/FlaggedRoute"
import { refreshOutletsIfNotExist } from "hooks/useSingleStoreUser"
import useFetchAllStoreList from "hooks/useFetchAllStoreList"
import { isInOrderPage } from "order-manager/utils/order_helper"
import { setupGrabXFeatureFlag } from "grabx"

import LazyLoadWrapper from "components/LazyLoadWrapper"
import { FeedbackWithHeader } from "pages/feedback"
import { Spin } from "@duxton-web/ui"
import { useMemoryMonitor } from "utils/metrics-monitor/useMemoryMonitor"
import { parseJSONSafely } from "utils/misc"
import { useGetEcommerceMerchantID } from "ecommerce/hooks/useGetEcommerceMerchantID"

const OrderStoreListComponent = lazy(() =>
  import("order-manager/pages/store/list/OrderStoreList"),
)

const OrderListComponent = lazy(() =>
  import("order-manager/pages/order/list/OrderList"),
)

const ProductsListComponent = lazy(() =>
  import("ecommerce/pages/products/list/ProductsList"),
)

const ProductDetailsComponent = lazy(() =>
  import("ecommerce/pages/products/detail/ProductDetails"),
)

const ProductBulkAddComponent = lazy(() =>
  import("ecommerce/pages/products/bulk/ProductBulkAdd"),
)

const StoreFrontCollectionListPage = lazy(() =>
  import("ecommerce/pages/storefront/collections/StorefrontCollectionList"),
)

const CreateCollectionPage = lazy(() =>
  import("ecommerce/pages/storefront/collections/CreateCollection"),
)

const LocationsPage = lazy(() => import("pages/multi-location"))

interface props {
  isLocaleLoading: boolean
}

const basePathToPageMapping = {
  [MENUS]: MENU_KEY,
  [INVENTORY]: INVENTORY_KEY,
  [CATALOGUES]: MENU_KEY,
  [ORDER_MANAGER_URL_PREFIX]: ORDER_KEY,
  [ECOMMERCE_URL_PREFIX]: ECOMMERCE_PRODUCT_KEY,
  [STOREFRONT_COLLECTION_URL_PREFIX]: STOREFRONT_COLLECTION_KEY,
} as const

// This is from troy-portal sidebar definition (see "troy-common/sidebar/sidebar-v2/SidebarMenuV2Data")
type TSidebarMenuConfig = { [key: string]: { show?: boolean } | undefined }

const isRouteValid = (
  mapping: TSidebarMenuConfig,
  routeKey: string,
): boolean => {
  return !!mapping[routeKey]?.show
}

export const NavigationContainer: FC<props> = ({ isLocaleLoading }: props) => {
  const {
    global: { messages },
    menu: { isMenuGroup, isFetchingOutlets, outlets },
  } = useSelector((state: AppState) => state)
  const history = useHistory()
  const location = useLocation()
  const currentLocale = useLocaleContext("locale")
  const switchLocale = useLocaleContext("switchLocale")
  const fetchAllStoreList = useFetchAllStoreList()
  const updateLanguage = (locale: string) => {
    if (location.pathname.includes("menu")) window.location.reload()
    sendEvent({
      action: EventAction.changeLanguage,
      label: new EventLabel({
        selectedLanguage: langCode2LangName(locale),
      }).build(),
    })
    switchLocale(locale)
    sessionInfo.updateLangShowed()
  }

  const [collapsed, setCollapsed] = useState(false)
  const [storeListLoaded, setStoreListLoaded] = useState<boolean>(false)
  const [isFeatureFlagLoading, setFeatureFlagLoading] = useState(true)

  useEffect(() => {
    // need to use static path on require to avoid ts and webpack error
    switch (currentLocale) {
      case "id":
        require(`dayjs/locale/id`)
        break
      case "vi":
        require(`dayjs/locale/vi`)
        break
      case "zh":
        require(`dayjs/locale/zh`)
        break
      case "th":
        require(`dayjs/locale/th`)
        break
      case "ms":
        require(`dayjs/locale/ms`)
        break
      default:
      // no need to import "en" since it's already loaded by default
    }
  }, [currentLocale])

  useMemoryMonitor()

  const onToggleCollapse = (state: boolean) => {
    setCollapsed(state)
  }

  const dispatch = useDispatch()

  const {
    merchantID: ecommerceMerchantID,
    isLoading: isLoadingEcommerceMerchantID,
  } = useGetEcommerceMerchantID()

  const newMessages = (useTranslationsMap({
    success: "common.success",
    error: "common.error",
  }) as unknown) as Messages

  const loadingTip = useTranslation("common.loadingConfig")

  const onResize = useCallback(() => {
    const isSideBarCollapsed = window.innerWidth <= 1024
    setCollapsed(isSideBarCollapsed)
  }, [])

  useEffect(() => {
    document.title = "Merchant Portal"

    onResize()
    window.addEventListener("resize", onResize)

    return () => {
      window.removeEventListener("resize", onResize)
    }
  }, [onResize])

  useEffect(() => {
    ;(async function waitGrabXSetup() {
      await setupGrabXFeatureFlag()
    })().finally(() => {
      setFeatureFlagLoading(false)
    })
  }, [setFeatureFlagLoading])

  const needToFetchStoreList =
    storeListLoaded === false && !isInOrderPage() && !isFeatureFlagLoading

  useEffect(() => {
    if (!needToFetchStoreList) {
      return
    }

    /**
     * Ensure that the feature flag (ff) has been fetched before fetching the store list
     */

    refreshOutletsIfNotExist(outlets, isFetchingOutlets, fetchAllStoreList)

    if (outlets) {
      setStoreListLoaded(true)
    }
  }, [
    outlets,
    isFetchingOutlets,
    fetchAllStoreList,
    needToFetchStoreList,
    isFeatureFlagLoading,
  ])

  const handleUpdateGenericMessages = () => {
    if (!isEqual(messages, newMessages)) {
      dispatch(actionCreator.global.setMessages(newMessages))
    }
  }
  useEffect(handleUpdateGenericMessages, [newMessages])

  const [currentPage, setCurrentPage] = useState<string>()
  useEffect(() => {
    const pathname = location.pathname
    const pageKey: string | undefined = Object.entries(
      basePathToPageMapping,
    ).find(([basePath]) => pathname.startsWith(basePath))?.[1]
    setCurrentPage(pageKey)

    TroyWebTracker.setPathname(pathname)
  }, [location.pathname])

  useEffect(() => {
    if (!isUndefined(currentPage)) {
      TroyWebTracker.sendEvent({
        category: EventCategory.pageOpen,
        action: EventAction.loadPage,
        labels: {
          FEATURE: currentPage,
        },
      })
    }
  }, [currentPage])

  useEffect(() => {
    TroyWebTracker.setIsMenuGroup(isMenuGroup)
  }, [isMenuGroup])

  const menuByRouteKey = useMemo(() => {
    const menus = createMenus()
    const res = keyBy(menus, "key") as TSidebarMenuConfig

    if (isEmpty(res)) {
      datadogRum.addError("Empty route registered", {
        tags: {
          context: "NavigationContainer",
          menus,
          menuByKey: res,
        },
      })
    }

    return res
  }, [])

  const showRoute = (routeKey: string): boolean => {
    return isRouteValid(menuByRouteKey, routeKey)
  }

  const canRenderNavigation =
    !isLocaleLoading &&
    !isFeatureFlagLoading &&
    !needToFetchStoreList &&
    !isLoadingEcommerceMerchantID

  return (
    <div className="main-layout">
      {/* eslint-disable-next-line no-underscore-dangle */}
      {window.__SHOULD_SHOW_SIDEBAR && (
        <div className={collapsed ? "sidebar-collapsed" : "sidebar-expanded"}>
          <TroySidebarMenu
            collapsed={collapsed}
            IssliderCollpased={onToggleCollapse}
            overrideSelectedPage={currentPage}
            overrideHandleItemClick={(k: string) => {
              switch (k) {
                case MENU_KEY:
                  TroyWebTracker.sendEvent({
                    category: EventCategory.sidebarNavigation,
                    action: EventAction.clickMenu,
                    labels: {
                      MAIN_PAGE: "MENU",
                    },
                  })
                  history.push(MENUS)
                  return false
                case INVENTORY_KEY:
                  TroyWebTracker.sendEvent({
                    category: EventCategory.sidebarNavigation,
                    action: EventAction.clickInventory,
                    labels: {
                      MAIN_PAGE: "INVENTORY",
                    },
                  })
                  history.push(INVENTORY)
                  return false
                case ORDER_KEY:
                  TroyWebTracker.sendEvent({
                    category: OrderManagerEventCategory.sidebarNavigation,
                    action: OrderManagerEventAction.clickOrders,
                    labels: {
                      MAIN_PAGE: "ORDER",
                    },
                  })
                  dispatch(actionCreator.orderManager.resetOrderList())
                  history.push(ORDER_MANAGER_URL_PREFIX)
                  return false
                case FEEDBACK_KEY:
                  TroyWebTracker.sendEvent({
                    category: EventCategory.sidebarNavigation,
                    action: EventAction.clickFeedback,
                    labels: {
                      MAIN_PAGE: "FEEDBACK",
                    },
                  })
                  history.push(FEEDBACK)
                  return false
                case ECOMMERCE_PRODUCT_KEY:
                  TroyWebTracker.sendEvent({
                    category: EcommerceEventCategory.sidebarNavigation,
                    action: EcommerceEventAction.clickEcommerceProduct,
                    labels: {
                      MAIN_PAGE: "ECOMMERCE_PRODUCT",
                    },
                  })
                  history.push(ECOMMERCE_URL_PREFIX)
                  return false
                case STOREFRONT_COLLECTION_KEY:
                  TroyWebTracker.sendEvent({
                    category: EcommerceEventCategory.sidebarNavigation,
                    action:
                      EcommerceEventAction.clickEcommerceStorefrontCollection,
                    labels: {
                      MAIN_PAGE: "ECOMMERCE_STOREFRONT_COLLECTION",
                    },
                  })
                  history.push(STOREFRONT_COLLECTION_URL_PREFIX)
                  return false
                case LOCATIONS_KEY:
                  TroyWebTracker.sendEvent({
                    category: EcommerceEventCategory.sidebarNavigation,
                    action: EcommerceEventAction.clickEcommerceLocations,
                    labels: {
                      MAIN_PAGE: "ECOMMERCE_LOCATIONS",
                    },
                  })
                  history.push(LOCATIONS)
                  return false
                default:
                  return true
              }
            }}
            trackTroyAnalytics={(
              _: string,
              eventAction: string,
              labelsString: any,
            ) => {
              if (eventAction !== TROY_CHANGE_LANGUAGE) return
              TroyWebTracker.sendEvent({
                category: EventCategory.sidebarNavigation,
                action: EventAction.changeLanguage,
                labels: {
                  ...parseJSONSafely(labelsString ?? "{}"),
                  MAIN_PAGE: "MENU",
                },
              })
            }}
            updateLanguage={updateLanguage}
          />
        </div>
      )}
      {(isLocaleLoading || isFeatureFlagLoading) && (
        <Spin
          fullscreen
          size="large"
          tip={loadingTip}
          style={{ zIndex: 2000 }}
        />
      )}
      {canRenderNavigation && (
        <div className="site-layout">
          <div
            className={`site-layout-content ${
              collapsed
                ? "site-layout-content-collapsed"
                : "site-layout-content-expanded"
            }`}
          >
            {/* <MenuHeader /> */}
            <Switch>
              {/* Menu routes */}
              <FlaggedRoute
                exact
                path={[MENUS, CATALOGUES]}
                flag={showRoute(MENU_KEY)}
              >
                {isMenuGroup ? (
                  <MenuGroupListWithHeader />
                ) : (
                  <MenuStoreListWithHeader />
                )}
              </FlaggedRoute>
              <FlaggedRoute
                exact
                path={[
                  `${MENUS}/:menuParentID/:tabKey?/:itemID?`,
                  `${CATALOGUES}/:menuParentID/:tabKey?/:itemID?`,
                ]}
                flag={showRoute(MENU_KEY)}
              >
                {isMenuGroup ? (
                  <MenuGroupEditorWithHeader />
                ) : (
                  <MenuEditorWithHeader />
                )}
              </FlaggedRoute>
              <FlaggedRoute
                exact
                path={[CREATE_MENU, CREATE_CATALOGUE]}
                flag={showRoute(MENU_KEY)}
              >
                {isMenuGroup ? (
                  <CreateMenuGroupWithHeader />
                ) : (
                  <Redirect to={MENUS} />
                )}
              </FlaggedRoute>
              <FlaggedRoute
                exact
                path={[
                  `${CREATE_MENU}/:menuParentID/:tabKey?/:itemID?`,
                  `${CREATE_CATALOGUE}/:menuParentID/:tabKey?/:itemID?`,
                ]}
                flag={showRoute(MENU_KEY)}
              >
                {isMenuGroup ? (
                  <CreateMenuGroupWithHeader />
                ) : (
                  <Redirect to={MENUS} />
                )}
              </FlaggedRoute>
              {/* Inventory routes */}
              <FlaggedRoute
                flag={showRoute(INVENTORY_KEY)}
                exact
                path={`${INVENTORY}`}
              >
                <InventoryListWithHeader />
              </FlaggedRoute>
              <FlaggedRoute
                exact
                path={`${INVENTORY}/:menuParentID/:tabKey?/:itemID?`}
                flag={showRoute(INVENTORY_KEY)}
              >
                <InventoryStoreDetailWithHeader />
              </FlaggedRoute>
              {/* Order Manager routes */}
              <FlaggedRoute
                flag={showRoute(ORDER_KEY)}
                exact
                path={ORDER_MANAGER_URL_PREFIX}
              >
                <LazyLoadWrapper data-testid="order-manager">
                  <OrderStoreListComponent />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(ORDER_KEY)}
                exact
                path={[
                  `${ORDER_MANAGER_URL_PREFIX}/:merchantID/:tabKey?/:orderID?`,
                ]}
              >
                <LazyLoadWrapper>
                  <OrderListComponent />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(FEEDBACK_KEY)}
                exact
                path={[
                  `${FEEDBACK}/:fragment?`,
                  `${FEEDBACK}/:fragment?/:fragmentID`,
                ]}
              >
                <FeedbackWithHeader />
              </FlaggedRoute>
              {/* Ecommerce routes */}
              <FlaggedRoute
                flag={showRoute(ECOMMERCE_PRODUCT_KEY)}
                exact
                path={ECOMMERCE_URL_PREFIX}
                render={() => (
                  <Redirect to={ECOMMERCE_PRODUCTS_LIST(ecommerceMerchantID)} />
                )}
              />
              <FlaggedRoute
                flag={showRoute(ECOMMERCE_PRODUCT_KEY)}
                exact
                path={ECOMMERCE_PRODUCTS()}
                render={({ match }) => (
                  <Redirect
                    to={ECOMMERCE_PRODUCTS_LIST(match.params.storeID)}
                  />
                )}
              />
              <FlaggedRoute
                flag={showRoute(ECOMMERCE_PRODUCT_KEY)}
                exact
                path={ECOMMERCE_PRODUCTS_LIST()}
              >
                <LazyLoadWrapper data-testid="products-list">
                  <ProductsListComponent />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(ECOMMERCE_PRODUCT_KEY)}
                exact
                path={ECOMMERCE_PRODUCTS_ADD()}
              >
                <LazyLoadWrapper data-testid="products-add">
                  <ProductDetailsComponent />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(ECOMMERCE_PRODUCT_KEY)}
                exact
                path={ECOMMERCE_PRODUCTS_BULK_ADD()}
              >
                <LazyLoadWrapper data-testid="products-bulk-add">
                  <ProductBulkAddComponent />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(ECOMMERCE_PRODUCT_KEY)}
                exact
                path={ECOMMERCE_PRODUCTS_EDIT()}
              >
                <LazyLoadWrapper data-testid="products-edit">
                  <ProductDetailsComponent />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(ECOMMERCE_PRODUCT_KEY)}
                exact
                path={ECOMMERCE_PRODUCTS_DETAIL()}
              >
                <LazyLoadWrapper data-testid="products-detail">
                  <ProductDetailsComponent />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(STOREFRONT_COLLECTION_KEY)}
                exact
                path={STOREFRONT_COLLECTION_URL_PREFIX}
                render={() => (
                  <Redirect
                    to={STOREFRONT_COLLECTION_LIST(ecommerceMerchantID)}
                  />
                )}
              />
              <FlaggedRoute
                flag={showRoute(STOREFRONT_COLLECTION_KEY)}
                exact
                path={STOREFRONT_COLLECTIONS_ADD()}
              >
                <LazyLoadWrapper data-testid="create-collection">
                  <CreateCollectionPage />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(STOREFRONT_COLLECTION_KEY)}
                exact
                path={STOREFRONT_COLLECTIONS_EDIT()}
              >
                <LazyLoadWrapper data-testid="edit-collection">
                  <CreateCollectionPage />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(STOREFRONT_COLLECTION_KEY)}
                exact
                path={STOREFRONT_COLLECTION_LIST()}
              >
                <LazyLoadWrapper data-testid="storefront-collection-list">
                  <StoreFrontCollectionListPage />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <FlaggedRoute
                flag={showRoute(LOCATIONS_KEY)}
                exact
                path={LOCATIONS}
              >
                <LazyLoadWrapper data-testid="locations">
                  <LocationsPage />
                </LazyLoadWrapper>
              </FlaggedRoute>
              <NotFoundRoute />
            </Switch>
          </div>
        </div>
      )}
      {/* To avoid race condition between feature flag usage and fetch. */}
      {!isFeatureFlagLoading && getUCMConfig("enableOrderManager") && (
        <OrderNotificationComponent />
      )}
    </div>
  )
}
